import { useEffect } from "react";
import {
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
} from "react-router";
import { Sentry } from "@/shared/clients/sentry.client";
import { ChunkErrorView } from "@/shared/components/ChunkErrorView";
import { ErrorPageView } from "@/shared/components/ErrorPageView";
import { isChunkLoadError } from "@/shared/utils/chunkLoadError.util";

const NOT_FOUND_TITLE = "404";
const NOT_FOUND_DESCRIPTION =
  "La page que vous recherchez n'existe pas.";
const GENERIC_ERROR_TITLE = "Une erreur est survenue";
const GENERIC_ERROR_DESCRIPTION =
  "Nous sommes désolés, une erreur inattendue s'est produite. Notre équipe a été notifiée et travaille à résoudre le problème.";

export const RouteErrorBoundary = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  useEffect(() => {
    if (error instanceof Error) {
      const isChunk = isChunkLoadError(error);
      Sentry.captureException(error, {
        tags: {
          errorBoundary: "route",
          ...(isChunk && { errorType: "chunk_load_error" }),
        },
        level: isChunk ? "warning" : "error",
      });
      return;
    }

    if (isRouteErrorResponse(error)) {
      Sentry.captureException(
        new Error(`Route Error: ${error.status} ${error.statusText}`),
        {
          tags: { errorBoundary: "route", statusCode: String(error.status) },
          extra: { data: error.data },
        },
      );
      return;
    }

    if (error) {
      Sentry.captureException(new Error(String(error)), { tags: { errorBoundary: "route" } });
    }
  }, [error]);

  const goHome = () => navigate("/");

  if (isChunkLoadError(error)) {
    return <ChunkErrorView />;
  }

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <ErrorPageView
          title={NOT_FOUND_TITLE}
          description={NOT_FOUND_DESCRIPTION}
          onBackHome={goHome}
          showSupportText={false}
        />
      );
    }

    return (
      <ErrorPageView
        title={String(error.status)}
        description={error.statusText}
        onBackHome={goHome}
        showSupportText={false}
      />
    );
  }

  return (
    <ErrorPageView
      title={GENERIC_ERROR_TITLE}
      description={GENERIC_ERROR_DESCRIPTION}
      onBackHome={goHome}
      showSupportText
    />
  );
};
